"use client";

import { forwardRef, MouseEvent } from "react";
import { Button, createPolymorphicComponent, Menu } from "@mantine/core";
import IHVButtonProps from "./IHVButtonProps";
import style from "./HVButton.module.css";
import HVText from "../HVText/HVText";
import clsx from "clsx";
import HVIcon from "../HVIcon/HVIcon";

const HVButton = createPolymorphicComponent<"button", IHVButtonProps>(
  forwardRef<HTMLButtonElement, IHVButtonProps>(
    (
      {
        variant = "Primary",
        children,
        text,
        textProps,
        buttonsGroup,
        activeKey,
        dropdownItems,
        isSplitButton,
        isSplitButtonIconVisible = true,
        onClick,
        buttonContainerProps,
        buttonMenuContainerProps,
        menuContainerProps,
        menuTargetContainerProps,
        menuDropdownContainerProps,
        menuItemContainerProps,
        leftSection,
        rightSection,
        ...rest
      }: IHVButtonProps,
      ref,
    ) => {
      // Split button
      if (isSplitButton && dropdownItems?.length) {
        return (
          <div className={style["div-container-split-button"]}>
            <Menu withinPortal={false} {...menuContainerProps}>
              <div className={style["div-menu-container"]}>
                <Button
                  variant={variant}
                  onClick={(e) => {
                    onClick?.(e);
                  }}
                  className={clsx(
                    style.root,
                    style[variant],
                    style["first-button"],
                  )}
                  {...buttonContainerProps}
                  leftSection={leftSection}
                  rightSection={rightSection}
                  {...rest}
                >
                  <HVText type="Body-sm" c="inherit" {...textProps}>
                    {text}
                  </HVText>
                </Button>

                {isSplitButtonIconVisible && (
                  <Menu.Target {...menuTargetContainerProps}>
                    <Button
                      variant="default"
                      className={clsx(
                        style.root,
                        style[variant],
                        style["last-button"],
                      )}
                      {...buttonMenuContainerProps}
                      {...rest}
                    >
                      <HVIcon
                        name="arrow-chevron-down"
                        svgProps={{ width: 20, height: 20 }}
                      />
                    </Button>
                  </Menu.Target>
                )}
              </div>

              <Menu.Dropdown
                className={style["button-dropdown"]}
                {...menuDropdownContainerProps}
              >
                {dropdownItems.map((item, idx) => (
                  <Menu.Item
                    className={style["button-dropdown-item"]}
                    key={idx}
                    onClick={item.onClick}
                    {...menuItemContainerProps}
                  >
                    {item.label}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          </div>
        );
      }

      // Button Group
      if (buttonsGroup && buttonsGroup.length > 0) {
        return (
          <div style={{ display: "flex" }}>
            {buttonsGroup.map((btn, index) => {
              const isFirst = index === 0;
              const isLast = index === buttonsGroup.length - 1;
              const isActive = activeKey === btn.key;

              return (
                <Button
                  key={btn.key}
                  component="button"
                  ref={ref}
                  onClick={btn.onClick}
                  disabled={btn.disabled}
                  className={clsx(
                    style.root,
                    style["ButtonGroup"],
                    isFirst && style.first,
                    isLast && style.last,
                    isActive && style.active,
                  )}
                  {...buttonContainerProps}
                  leftSection={leftSection}
                  rightSection={rightSection}
                  {...rest}
                >
                  <HVText type="Body-sm" c="inherit" {...textProps}>
                    {btn.text}
                  </HVText>
                </Button>
              );
            })}
          </div>
        );
      }

      // Normal single button
      return (
        <Button
          component="button"
          ref={ref}
          variant={variant}
          styles={{
            section: {
              marginInlineStart: "var(--hv-spacing-1)",
            },
          }}
          className={clsx(style.root, style[variant])}
          {...buttonContainerProps}
          leftSection={leftSection}
          rightSection={rightSection}
          onClick={(e) => {
            onClick?.(e);
          }}
          {...rest}
        >
          {text && (
            <HVText type="Body-sm" c="inherit" {...textProps}>
              {text}
            </HVText>
          )}
          {children}
        </Button>
      );
    },
  ),
);

export default HVButton;
